import { StaggeredAnimationItem } from "#components/animation/StaggeredAnimationItem.tsx";
import { OptionRow } from "#components/ui/option-row/OptionRow.tsx";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import type { AskedQuestion, QuestionAnswer } from "#types/types.ts";
import { Button, Div, Flex, Text, TextField } from "@hopper-ui/components";
import { AngleLeftIcon, AngleRightIcon, DismissIcon } from "@hopper-ui/icons";
import { Fragment, useCallback, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type MouseEvent } from "react";
import ReactMarkdown from "react-markdown";
import "./InteractiveQuestionCard.css";
import "./InteractiveQuestionCard.dark.css";

interface Props {
    questions: AskedQuestion[];
    // The turn's prompt feature, for tracking only.
    promptFeature?: string;
    // Reports every answer at once, in the order asked, after the last question.
    onAnswer: (answers: QuestionAnswer[]) => void;
    onClose: () => void;
}

export function InteractiveQuestionCard({ questions, promptFeature, onAnswer, onClose }: Props) {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    // The answers so far, by question index. An answer is a non-empty list: one item, or one per option of a multiselect.
    const [answers, setAnswers] = useState<Record<number, string[]>>({});
    const totalQuestions = questions.length;
    const isFirstQuestion = useCallback(() => currentQuestionIndex === 0, [currentQuestionIndex]);
    const isLastQuestion = useCallback(() => currentQuestionIndex === totalQuestions - 1, [currentQuestionIndex, totalQuestions]);
    const nextQuestion = useCallback(() => setCurrentQuestionIndex(index => Math.min(index + 1, totalQuestions - 1)), [totalQuestions]);
    const previousQuestion = useCallback(() => setCurrentQuestionIndex(index => Math.max(index - 1, 0)), []);
    const getCurrentQuestion = useCallback(() => questions[currentQuestionIndex] ?? null, [questions, currentQuestionIndex]);

    const { tShared } = useSharedTranslation();
    const { trackEvent } = useTracking();

    const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
    const [customValue, setCustomValue] = useState("");

    const optionsContainerRef = useRef<HTMLDivElement>(null);
    const textFieldContainerRef = useRef<HTMLDivElement>(null);
    const prevQuestionIndexRef = useRef(currentQuestionIndex);
    const directionRef = useRef<"forward" | "backward" | null>(null);

    if (currentQuestionIndex !== prevQuestionIndexRef.current) {
        directionRef.current = currentQuestionIndex > prevQuestionIndexRef.current ? "forward" : "backward";
        prevQuestionIndexRef.current = currentQuestionIndex;
    }

    useEffect(() => {
        setSelectedLabels([]);
        setCustomValue("");

        // Keep focus inside the flow when the animated question body remounts.
        // Imperative focus preserves the browser's input-modality heuristic, so
        // pointer users do not receive a synthetic focus-visible treatment.
        const animationFrame = requestAnimationFrame(() => {
            const firstOption = optionsContainerRef.current?.querySelector<HTMLElement>("button");
            if (firstOption) {
                firstOption.focus({ preventScroll: true });
            } else {
                textFieldContainerRef.current?.querySelector<HTMLElement>("input")?.focus({ preventScroll: true });
            }
        });

        return () => cancelAnimationFrame(animationFrame);
    }, [currentQuestionIndex]);

    const currentQuestion = getCurrentQuestion();

    const advanceOrClose = useCallback((answer: string[]) => {
        const collected = { ...answers, [currentQuestionIndex]: answer };

        if (isLastQuestion()) {
            // The page swaps the card for the composer once the answers are sent. onClose is only for the explicit X button.
            onAnswer(questions.map(({ question }, index) => ({ question, answer: collected[index] ?? [tShared("interactiveQuestion.noPreference")] })));

            return;
        }
        setAnswers(collected);
        nextQuestion();
        setCustomValue("");
        setSelectedLabels([]);
    }, [answers, currentQuestionIndex, isLastQuestion, nextQuestion, onAnswer, questions, tShared]);

    const handleMultiSelectToggle = useCallback((label: string) => {
        setSelectedLabels(prev =>
            prev.includes(label)
                ? prev.filter(l => l !== label)
                : [...prev, label]
        );
    }, []);

    const handleMultiSelectConfirm = useCallback(() => {
        if (selectedLabels.length === 0) { return; }
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Interactive Question Card",
            Trigger: "Option Selected",
            Feature: promptFeature,
            "Option": selectedLabels.join(", "),
            "Question Index": currentQuestionIndex + 1,
            "Total Questions": totalQuestions
        });
        advanceOrClose(selectedLabels);
    }, [selectedLabels, trackEvent, promptFeature, currentQuestionIndex, totalQuestions, advanceOrClose]);

    const handleCustomSubmit = useCallback(() => {
        const trimmed = customValue.trim();
        if (!trimmed) { return; }
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Interactive Question Card",
            Trigger: "Custom Answer Submitted",
            Feature: promptFeature,
            "Question Index": currentQuestionIndex + 1,
            "Total Questions": totalQuestions
        });
        advanceOrClose([trimmed]);
    }, [customValue, trackEvent, promptFeature, currentQuestionIndex, totalQuestions, advanceOrClose]);

    const handleOptionSelect = useCallback((label: string) => {
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Interactive Question Card",
            Trigger: "Option Selected",
            Feature: promptFeature,
            "Question Index": currentQuestionIndex + 1,
            "Total Questions": totalQuestions
        });
        advanceOrClose([label]);
    }, [trackEvent, promptFeature, currentQuestionIndex, totalQuestions, advanceOrClose]);

    const handleSkip = useCallback(() => {
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Interactive Question Card",
            Trigger: "Skipped",
            Feature: promptFeature,
            "Question Index": currentQuestionIndex + 1,
            "Total Questions": totalQuestions
        });
        advanceOrClose([tShared("interactiveQuestion.noPreference")]);
    }, [trackEvent, promptFeature, currentQuestionIndex, totalQuestions, tShared, advanceOrClose]);

    const handleDismiss = useCallback(() => {
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Interactive Question Card",
            Trigger: "Dismissed",
            Feature: promptFeature,
            "Question Index": currentQuestionIndex + 1,
            "Total Questions": totalQuestions
        });
        onClose();
    }, [trackEvent, promptFeature, currentQuestionIndex, totalQuestions, onClose]);

    const handleOptionsKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
        const container = optionsContainerRef.current;
        if (!container) { return; }

        const buttons = Array.from(container.querySelectorAll<HTMLElement>("button"));
        const currentIndex = buttons.findIndex(b => b === document.activeElement);
        const columnCount = Math.max(
            window.getComputedStyle(container).gridTemplateColumns.split(" ").filter(Boolean).length,
            1
        );
        const currentRow = Math.floor(currentIndex / columnCount);

        if (e.key === "ArrowDown") {
            e.preventDefault();
            const nextRowIndex = currentIndex + columnCount;
            if (nextRowIndex < buttons.length) {
                buttons[nextRowIndex].focus();
            } else {
                textFieldContainerRef.current?.querySelector<HTMLElement>("input")?.focus();
            }
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            const previousRowIndex = currentIndex - columnCount;
            if (previousRowIndex >= 0) {
                buttons[previousRowIndex].focus();
            }
        } else if (e.key === "ArrowLeft") {
            const previousColumnIndex = currentIndex - 1;
            if (columnCount > 1 && previousColumnIndex >= 0 && Math.floor(previousColumnIndex / columnCount) === currentRow) {
                e.preventDefault();
                buttons[previousColumnIndex].focus();
            } else if (columnCount === 1 && !isFirstQuestion()) {
                e.preventDefault();
                previousQuestion();
            }
        } else if (e.key === "ArrowRight") {
            const nextColumnIndex = currentIndex + 1;
            if (columnCount > 1) {
                if (nextColumnIndex < buttons.length && Math.floor(nextColumnIndex / columnCount) === currentRow) {
                    e.preventDefault();
                    buttons[nextColumnIndex].focus();
                }
            } else {
                const question = getCurrentQuestion();
                if (question && !question.multiselect && currentIndex >= 0) {
                    e.preventDefault();
                    buttons[currentIndex].click();
                } else if (question?.multiselect && !isLastQuestion()) {
                    e.preventDefault();
                    nextQuestion();
                }
            }
        }
    }, [getCurrentQuestion, isFirstQuestion, isLastQuestion, previousQuestion, nextQuestion]);

    const handleCardClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
        // If the click didn't land on a focusable child, restore keyboard focus
        // so the user can continue navigating with the keyboard after clicking the card background.
        if (!e.currentTarget.contains(document.activeElement) || document.activeElement === document.body) {
            const firstButton = optionsContainerRef.current?.querySelector<HTMLElement>("button");
            if (firstButton) {
                firstButton.focus();
            } else {
                textFieldContainerRef.current?.querySelector<HTMLElement>("input")?.focus();
            }
        }
    }, []);

    const handleFooterKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "ArrowLeft" && !isFirstQuestion()) {
            e.preventDefault();
            previousQuestion();
        } else if (e.key === "ArrowRight" && !isLastQuestion()) {
            e.preventDefault();
            nextQuestion();
        }
    }, [isFirstQuestion, isLastQuestion, previousQuestion, nextQuestion]);

    const handleCustomKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleCustomSubmit();
        } else if (e.key === "ArrowUp") {
            const container = optionsContainerRef.current;
            if (container) {
                const buttons = Array.from(container.querySelectorAll<HTMLElement>("button"));
                if (buttons.length > 0) {
                    e.preventDefault();
                    buttons[buttons.length - 1].focus();
                }
            }
        } else if (e.key === "ArrowRight" && customValue.trim()) {
            e.preventDefault();
            handleCustomSubmit();
        } else if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && !customValue) {
            if (e.key === "ArrowLeft" && !isFirstQuestion()) {
                e.preventDefault();
                previousQuestion();
            } else if (e.key === "ArrowRight" && !isLastQuestion()) {
                e.preventDefault();
                nextQuestion();
            }
        }
    }, [handleCustomSubmit, customValue, isFirstQuestion, isLastQuestion, previousQuestion, nextQuestion]);

    if (!currentQuestion) {
        return null;
    }

    const questionNumber = currentQuestionIndex + 1;
    const hasOptions = currentQuestion.options && currentQuestion.options.length > 0;
    const isMultiSelect = !!currentQuestion.multiselect;
    const footerButtonHeight = "2.375rem";

    return (
        <Flex
            className="wai-interactive-question-card"
            direction="column"
            position="relative"
            zIndex={100}
            overflow="hidden"
            backgroundColor="neutral-weakest"
            borderRadius="rounded-lg"
            padding="inset-md"
            UNSAFE_maxHeight="min(30rem, 70vh)"
            onClick={handleCardClick}
        >
            {/* Header row — stable, outside the question transition. */}
            <Flex
                justifyContent="space-between"
                alignItems="center"
                marginBottom="inline-xs"
                gap="inline-sm"
                shrink={0}
            >
                <Text size="xs" fontWeight="body-xs-medium" color="neutral-weak">
                    {currentQuestion.header ?? tShared("interactiveQuestion.promptLabel")}
                </Text>
                <Flex alignItems="center" gap="inline-xs" shrink="0">
                    {totalQuestions > 1 && (
                        <>
                            <Button
                                variant="ghost-secondary"
                                size="xs"
                                aria-label={tShared("interactiveQuestion.previousQuestion")}
                                onPress={previousQuestion}
                                isDisabled={isFirstQuestion()}
                            >
                                <AngleLeftIcon size="sm" />
                            </Button>
                            <Text size="xs" color="neutral-weak">
                                {tShared("interactiveQuestion.questionProgress", { current: questionNumber, total: totalQuestions })}
                            </Text>
                            <Button
                                variant="ghost-secondary"
                                size="xs"
                                aria-label={tShared("interactiveQuestion.nextQuestion")}
                                onPress={nextQuestion}
                                isDisabled={isLastQuestion()}
                            >
                                <AngleRightIcon size="sm" />
                            </Button>
                        </>
                    )}
                    <Button
                        variant="ghost-secondary"
                        size="xs"
                        aria-label={tShared("close")}
                        onPress={handleDismiss}
                    >
                        <DismissIcon size="sm" />
                    </Button>
                </Flex>
            </Flex>
            <Text
                size="md"
                fontWeight="body-md-medium"
                color="neutral"
                marginBottom="inline-sm"
            >
                <ReactMarkdown
                    allowedElements={["p", "strong", "em", "code", "del"]}
                    unwrapDisallowed
                    components={{ p: Fragment }}
                >
                    {currentQuestion.question}
                </ReactMarkdown>
            </Text>

            {/* Animated body — remounted on each question change to trigger the enter animation */}
            <Div
                key={currentQuestionIndex}
                flexShrink={1}
                minHeight={0}
                overflow="hidden auto"
                className={`wai-interactive-question-body${directionRef.current ? ` wai-interactive-question-body--${directionRef.current}` : ""}`}
            >
                {/* Predefined options */}
                {hasOptions && (
                    <Div
                        ref={optionsContainerRef}
                        onKeyDown={handleOptionsKeyDown}
                        className="wai-interactive-question-options"
                        marginBottom="inline-sm"
                    >
                        {currentQuestion.options!.map((option, index) => (
                            <StaggeredAnimationItem
                                key={option.label}
                                style={{
                                    "--wai-staggered-animation-item-animation": "wai-reveal",
                                    "--wai-staggered-animation-item-delay": `${index * 40}ms`
                                } as CSSProperties}
                            >
                                <OptionRow
                                    label={option.label}
                                    index={index}
                                    isSelected={isMultiSelect ? selectedLabels.includes(option.label) : undefined}
                                    isLast={!isMultiSelect && isLastQuestion()}
                                    onPress={() => isMultiSelect
                                        ? handleMultiSelectToggle(option.label)
                                        : handleOptionSelect(option.label)
                                    }
                                />
                            </StaggeredAnimationItem>
                        ))}
                    </Div>
                )}

                {/* Multiselect footer: selected count + Skip + Confirm */}
                {isMultiSelect && (
                    <Flex justifyContent="space-between" alignItems="center" onKeyDown={handleFooterKeyDown}>
                        <Text size="xs" color="neutral-weak">
                            {selectedLabels.length === 0
                                ? tShared("interactiveQuestion.multiSelectHint")
                                : tShared("interactiveQuestion.selectedCount", { count: selectedLabels.length })}
                        </Text>
                        <Flex gap="inline-sm">
                            <Button
                                variant="ghost-secondary"
                                size="sm"
                                onPress={handleSkip}
                                UNSAFE_height={footerButtonHeight}
                            >
                                {tShared("interactiveQuestion.skip")}
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                isDisabled={selectedLabels.length === 0}
                                onPress={handleMultiSelectConfirm}
                                UNSAFE_height={footerButtonHeight}
                                className="wai-interactive-question-confirm"
                            >
                                {tShared("interactiveQuestion.confirm")}
                            </Button>
                        </Flex>
                    </Flex>
                )}

                {/* Text input — hidden in multiselect mode to avoid dual-CTA with Confirm button */}
                {!isMultiSelect && (
                    <Flex ref={textFieldContainerRef} alignItems="center" gap="inline-xs">
                        <Flex grow={1}>
                            <TextField
                                value={customValue}
                                onChange={setCustomValue}
                                onKeyDown={handleCustomKeyDown}
                                enterKeyHint="send"
                                placeholder={tShared(hasOptions ? "interactiveQuestion.textPlaceholderWithOptions" : "interactiveQuestion.textPlaceholder")}
                                size="sm"
                                isFluid
                                inputGroupProps={{ borderRadius: "pill" }}
                                autoFocus={!hasOptions}
                            />
                        </Flex>
                        {customValue.trim() ? (
                            <Button
                                variant="primary"
                                size="sm"
                                onPress={handleCustomSubmit}
                                UNSAFE_height={footerButtonHeight}
                                borderRadius="pill"
                            >
                                {tShared("interactiveQuestion.submit")}
                            </Button>
                        ) : (
                            <Button
                                variant="ghost-secondary"
                                size="sm"
                                UNSAFE_height={footerButtonHeight}
                                onPress={handleSkip}
                                borderRadius="pill"
                            >
                                {tShared("interactiveQuestion.skip")}
                            </Button>
                        )}
                    </Flex>
                )}
            </Div>
        </Flex>
    );
}
